import { ApolloProvider } from '@apollo/client';
import type { AuthProviderConfig } from '@cmg/auth';
import { AuthProvider } from '@cmg/auth';
import { ErrorBoundary, GlobalStyle, ThemeProvider } from '@cmg/common';
import type { DesignSystemProviderProps } from '@cmg/design-system';
import { DesignSystemProvider } from '@cmg/design-system';
import { I18nProvider } from '@cmg/i18n';
import React from 'react';
import { Provider } from 'react-redux';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { createGlobalStyle } from 'styled-components';

import graphqlApiClient from '../../common/api/graphqlApiClient';
import { IdentityAppLayout } from '../../common/components/layout/design-system/app-layout/IdentityAppLayout';
import { getAppSettings } from '../../common/config/appSettings';
import { AppSettingsContextProvider } from '../../common/config/context/AppSettingsContext';
import store from '../../common/redux/store';
import routeFactory from '../../common/util/routeFactory';
import { ReactRouterMUILink } from '../../design-system/react-router/ReactRouterMUILink';

const appSettings = getAppSettings();
const NormalizedHeightRootCss = createGlobalStyle`
  html, body, #root {
    height: 100%;
  }
`;

const designSystemProviderProps: DesignSystemProviderProps = {
  themeConfig: { LinkComponent: ReactRouterMUILink },
};

const authConfig: AuthProviderConfig = {
  env: {
    isDevelopment: appSettings.env.isDevelopment,
    rootDomain: appSettings.env.rootDomain,
    secureCookies: appSettings.env.secureCookies,
  },
  client: {
    basename: appSettings.client.basename,
  },
  logging: {
    appName: 'Identity',
    mixPanelToken: appSettings.logging.mixPanelToken,
  },
  auth: {
    clientId: appSettings.auth.clientId,
    oidcAuthorityBaseUrl: appSettings.auth.oidcAuthorityBaseUrl,
    tokenRenewalInterval: appSettings.auth.tokenRenewalIntervalInMinutes,
    scopes: ['mgmt_api:full', 'xc:full'],
  },
  actions: {
    onLogout: () => {
      window.location.href = routeFactory.loggedOut.getAbsoluteUrlPath();
    },
  },
};

const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const router = createBrowserRouter(
  [
    {
      path: '*',
      element: (
        <ErrorBoundary>
          <IdentityAppLayout />
        </ErrorBoundary>
      ),
    },
  ],
  {
    basename: appSettings.client.basename,
    future: {
      v7_relativeSplatPath: true,
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
      v7_partialHydration: true,
      v7_skipActionErrorRevalidation: true,
    },
  }
);

const App: React.FC = () => (
  <AuthProvider authConfig={authConfig}>
    <I18nProvider initialLocale="en-US" timeZone={browserTimeZone}>
      <Provider store={store}>
        <AppSettingsContextProvider>
          <GlobalStyle />
          <NormalizedHeightRootCss />
          <ApolloProvider client={graphqlApiClient}>
            <DesignSystemProvider {...designSystemProviderProps}>
              <ThemeProvider>
                <RouterProvider router={router} future={{ v7_startTransition: true }} />
              </ThemeProvider>
            </DesignSystemProvider>
          </ApolloProvider>
        </AppSettingsContextProvider>
      </Provider>
    </I18nProvider>
  </AuthProvider>
);

export default App;
